import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Alert } from 'react-bootstrap';
import ReactSelect from 'react-select';
import {
  CREATE_NCR_MUTATION,
  NCRCOMPANYLIST_LOOKUP_QUERY,
  NCRPROJECTLIST_LOOKUP_QUERY,
  NCRJOBLIST_LOOKUP_QUERY,
  NCRSUPPLIERS_LOOKUP_QUERY,
  NCRCHAMPION_LOOKUP_QUERY,
  NCRINITIATOR_LOOKUP_QUERY,
  NCRSOURCES_LOOKUP_QUERY,
  NCRREASONS_LOOKUP_QUERY,
  NCRLOCATIONS_LOOKUP_QUERY,
  NCRAREAS_LOOKUP_QUERY
} from '../../../timesheet/queries';
import { fetchFusionManagePicklist, fetchFusionManagePicklistDirect, mergePicklists } from './fusionManageUtils';
import {
  extractUniqueGlobalSites,
  extractUniqueSites,
  extractUniqueDepartments,
  deriveGlobalSiteFromSite
} from './locationUtils';
import { useFormSubmission } from '../../hooks/useFormSubmission';
import {
  normalizeSelectField
} from '../../utils/formDataTransformers';
import { WORKSPACE_IDS } from '../../constants/fusionManageConstants';
import InfoTooltip from '../InfoTooltip';

// Debounce hook for optimizing filtered option fetches
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

function IPIForm({ onFormSubmitSuccess, onFormSubmitError }) {
  const initialState = {
    // Auto-generated fields
    "Number": "", // auto generated

    // Required fields regardless of Project or Technology IPI
    "ProjectOrTechnology": null, // dropdown search - null for no selection
    "NCRTitle": "", // input - will be generated from IPI data
    "Description": "", // input box
    "IncidentDate": "", // date input - will use current date
    "CostOfNCR": "", // input box

    // IPI-specific fields
    "DefectType": null, // dropdown - null for no selection
    "PartNumber": "",
    "AffectedQuantity": "",
    "ClockingCode": null, // dropdown - null for no selection
    "ProjectID": null, // dropdown - null for no selection
    "ProjectName": null, // dropdown - null for no selection
    "JobNumber": null, // dropdown - null for no selection
    "JobCode": null, // dropdown - null for no selection
    "IsReworkRequired": null, // dropdown - null for no selection
    "AffectedProduct": null, // dropdown - null for no selection
    "AffectedVariant": null, // dropdown - null for no selection
    "IsPCBRelated": null, // dropdown - null for no selection
    "PCBSerialNumber": "",
    "IssueType": null, // dropdown - null for no selection
    "IPIType": null,

    // Required fields regardless of Project or Technology IPI
    "GlobalSiteAssigned": null, // dropdown search - null for no selection
    "SiteAssigned": null, // dropdown search - null for no selection
    "DepartmentAssigned": null, // dropdown search - null for no selection
    "GlobalSiteRaisedBy": null, // dropdown search - null for no selection
    "SiteRaisedBy": null, // dropdown search - null for no selection
    "DepartmentRaisedBy": null, // dropdown search - null for no selection
    "RaisedBy": "", // input - text field that submits to NAME_AND_SURNAME
    "RaisedDate": "", // date input

    // Legacy fields for backward compatibility
    "CaseTitle": "",
    "CaseSummary": "",
    "ProjectRelated": true
  };

  const [formData, setFormData] = useState(initialState);
  const [projects, setProjects] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [champions, setChampions] = useState([]);
  const [initiator, setInitiator] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [sources, setSources] = useState([]);
  const [areas, setAreas] = useState([]);
  
  // New state variables for enhanced form
  const [productNames, setProductNames] = useState([]);
  const [productVariants, setProductVariants] = useState([]);
  const [globalSites, setGlobalSites] = useState([]);
  const [sites, setSites] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [ncrChampions, setNcrChampions] = useState([]);
  const [raisedByUsers, setRaisedByUsers] = useState([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [fusionManageStatus, setFusionManageStatus] = useState(null);
  const [fusionManageMessage, setFusionManageMessage] = useState('');
  const alertRef = useRef(null);
  // Fusion Manage picklist state
  const [fusionDefectTypes, setFusionDefectTypes] = useState([]);        // IPI Type
  const [fusionReasonCodes, setFusionReasonCodes] = useState([]);        // Defect Type (WS 406)
  const [fusionClockingCodes, setFusionClockingCodes] = useState([]);    // Clocking Code (WS 637)
  const [fusionYesNo, setFusionYesNo] = useState([]);                    // Yes/No fields
  const [fusionProductTypes, setFusionProductTypes] = useState([]);      // Affected Product (WS 539)
  const [fusionIpiActionTaken, setFusionIpiActionTaken] = useState([]);  // IPI Action Taken
  const [fusionLocations, setFusionLocations] = useState([]);            // Locations (WS 506)
  const [fusionNCRChampions, setFusionNCRChampions] = useState([]);      // Champions (WS 538)
  const [fusionDepartments, setFusionDepartments] = useState([]);        // Departments

  // Parsed location cache and filtered option state (mirrors NCR form)
  const [parsedLocations, setParsedLocations] = useState({
    globalSites: [],
    sites: [],
    departments: []
  });
  const [filteredSiteOptionsAssigned, setFilteredSiteOptionsAssigned] = useState([]);
  const [filteredDepartmentOptionsAssigned, setFilteredDepartmentOptionsAssigned] = useState([]);
  const [filteredChampionOptionsAssigned, setFilteredChampionOptionsAssigned] = useState([]);
  const [filteredSiteOptionsRaisedBy, setFilteredSiteOptionsRaisedBy] = useState([]);
  const [filteredDepartmentOptionsRaisedBy, setFilteredDepartmentOptionsRaisedBy] = useState([]);
  const [validGlobalSiteOptionsAssigned, setValidGlobalSiteOptionsAssigned] = useState([]);
  const [validGlobalSiteOptionsRaisedBy, setValidGlobalSiteOptionsRaisedBy] = useState([]);
  const [loadingGlobalSiteOptions, setLoadingGlobalSiteOptions] = useState(false);
  const [loadingFilteredOptions, setLoadingFilteredOptions] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingAreas, setLoadingAreas] = useState(false);
  const [loadingChampions, setLoadingChampions] = useState(false);
  const [loadingFusionData, setLoadingFusionData] = useState(true);

  // Helper function to safely fetch GraphQL data (moved before useEffects that use it)
  const fetchGraphQL = useCallback(async (query, variables = {}) => {
    try {
      // Convert gql DocumentNode to string if needed
      let queryString = query;
      if (query && typeof query === 'object' && query.loc) {
        // It's a gql DocumentNode - extract the query string
        queryString = query.loc.source.body;
      } else if (query && typeof query === 'object' && query.definitions) {
        // Alternative way to get query string from DocumentNode
        queryString = query.loc?.source?.body || query.definitions[0]?.loc?.source?.body || String(query);
      }
      
      console.log('[IPIForm] fetchGraphQL - Query:', queryString.substring(0, 100) + '...');
      console.log('[IPIForm] fetchGraphQL - Variables:', variables);
      
      const response = await fetch('/api/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query: queryString, variables }),
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      
      console.log('[IPIForm] fetchGraphQL - Response:', data);
      
      if (data.errors) {
        console.error('[IPIForm] GraphQL errors:', data.errors);
      }
      
      return data.data;
    } catch (error) {
      console.error(`[IPIForm] GraphQL fetch error:`, error);
      return null;
    }
  }, []);

  // Helpers
  const sanitizeChampionOption = (option) => {
    if (!option) return option;
    const rawLabel = option.label || option.value || '';
    const cleanedLabel = rawLabel.split(' - ')[0]?.trim() || rawLabel;
    return {
      ...option,
      value: String(option.value || option.id || cleanedLabel),
      label: cleanedLabel
    };
  };

  // Parse Project ID from label: "25W0159 - Project Name" or "25W0159-Project Name" -> "25W0159"
  // Extracts everything BEFORE the dash and removes leading/trailing whitespace
  const parseProjectIdFromLabel = (label) => {
    if (!label) return '';
    const trimmed = String(label).trim();
    // Find the first dash (prefer " - " with spaces, then just "-")
    const dashIndex = trimmed.indexOf(' - ') !== -1 ? trimmed.indexOf(' - ') : trimmed.indexOf('-');
    if (dashIndex === -1) {
      // No dash found, return everything before first space or the whole string if no space
      const firstSpaceIndex = trimmed.indexOf(' ');
      const result = firstSpaceIndex === -1 ? trimmed : trimmed.substring(0, firstSpaceIndex);
      return result.trim(); // Remove leading/trailing whitespace
    }
    // Extract everything before the dash (handles alphanumeric IDs like "25W0159")
    const beforeDash = trimmed.substring(0, dashIndex);
    return beforeDash.trim(); // Remove leading/trailing whitespace
  };

  // Parse Project Name from label: "25W0159 - Project Name" or "25W0159-Project Name" -> "Project Name"
  // Extracts everything AFTER the dash and removes leading/trailing whitespace
  const parseProjectNameFromLabel = (label) => {
    if (!label) return '';
    const trimmed = String(label).trim();
    // Try " - " first, then just "-"
    let dashIndex = trimmed.indexOf(' - ');
    let skipLength = 3; // Skip " - "
    if (dashIndex === -1) {
      dashIndex = trimmed.indexOf('-');
      skipLength = 1; // Skip "-"
    }
    if (dashIndex === -1) return '';
    // Extract everything after the dash
    const afterDash = trimmed.substring(dashIndex + skipLength);
    return afterDash.trim(); // Remove leading/trailing whitespace
  };

  // Format options for ReactSelect - merge Epicor defaults with Fusion Manage (memoized)
  const ipiTypeOptions = useMemo(() => mergePicklists([], fusionDefectTypes), [fusionDefectTypes]);

  const reasonCodeOptions = useMemo(() => mergePicklists(
    [
      { value: 'Dimensional', label: 'Dimensional' },
      { value: 'Surface', label: 'Surface' },
      { value: 'Functional', label: 'Functional' },
      { value: 'Material', label: 'Material' },
      { value: 'Assembly', label: 'Assembly' },
      { value: 'Documentation', label: 'Documentation' },
      { value: 'Other', label: 'Other' }
    ],
    fusionReasonCodes
  ), [fusionReasonCodes]);

  const clockingCodeOptions = useMemo(() => mergePicklists([], fusionClockingCodes), [fusionClockingCodes]);

  const yesNoOptions = useMemo(() => mergePicklists(
    [
      { value: 'Yes', label: 'Yes' },
      { value: 'No', label: 'No' }
    ],
    fusionYesNo
  ), [fusionYesNo]);

  // Is Rework Required dropdown options - uses CUSTOM_LOOKUP_YES_NO picklist from Fusion Manage
  // Note: Only "Yes" and "No" are valid options - "To Be Determined" is not in the picklist
  const reworkOptions = useMemo(() => mergePicklists(
    [
      { value: 'Yes', label: 'Yes' },
      { value: 'No', label: 'No' }
    ],
    fusionYesNo
  ), [fusionYesNo]);

  // Helper function to extract text after dash (e.g., "Hampton - Apex Controller" -> "Apex Controller")
  const extractAfterDash = (str) => {
    if (!str || typeof str !== 'string') return str;
    const dashIndex = str.indexOf(' - ');
    if (dashIndex !== -1) {
      return str.substring(dashIndex + 3).trim();
    }
    // Also check for single dash without spaces
    const singleDashIndex = str.indexOf('-');
    if (singleDashIndex !== -1) {
      return str.substring(singleDashIndex + 1).trim();
    }
    return str.trim();
  };

  const productTypeOptions = useMemo(() => {
    console.log('[IPIForm] productTypeOptions useMemo - fusionProductTypes state:', fusionProductTypes);
    console.log('[IPIForm] productTypeOptions useMemo - fusionProductTypes length:', fusionProductTypes?.length || 0);
    console.log('[IPIForm] productTypeOptions useMemo - fusionProductTypes type:', typeof fusionProductTypes);
    
    if (!fusionProductTypes || fusionProductTypes.length === 0) {
      console.warn('[IPIForm] productTypeOptions - fusionProductTypes is empty or undefined');
      return [];
    }

    const merged = mergePicklists([], fusionProductTypes);
    
    // Format options to show only text after dash in the dropdown, but keep original value for submission
    const formattedOptions = merged.map((product) => {
      const originalValue = product.value || product.label || '';
      const originalLabel = product.label || product.value || '';
      
      // Extract text after dash for display
      const displayValue = extractAfterDash(originalValue);
      const displayLabel = extractAfterDash(originalLabel);
      
      return {
        value: originalValue, // Keep original for submission
        label: displayLabel || displayValue, // Show shortened version in dropdown
        originalValue: originalValue, // Store original for reference
        originalLabel: originalLabel
      };
    });
    
    console.log('[IPIForm] productTypeOptions generated:', formattedOptions.length, 'options from', fusionProductTypes.length, 'products');
    if (formattedOptions.length > 0) {
      console.log('[IPIForm] Sample productTypeOption:', formattedOptions[0]);
    } else if (fusionProductTypes.length > 0) {
      console.error('[IPIForm] CRITICAL: fusionProductTypes has', fusionProductTypes.length, 'items but productTypeOptions is empty!');
      console.error('[IPIForm] All fusionProductTypes:', fusionProductTypes);
    }
    return formattedOptions;
  }, [fusionProductTypes]);
  const productVariantOptions = useMemo(() => (productVariants || []).map((variant) => ({
    value: variant.value,
    label: variant.label,
  })), [productVariants]);
  const ipiActionTakenOptions = useMemo(() => mergePicklists([], fusionIpiActionTaken), [fusionIpiActionTaken]);

  const issueTypeOptions = useMemo(() => [
    { value: 'Design', label: 'Design Issue' },
    { value: 'Manufacturing', label: 'Manufacturing Issue' },
    { value: 'Process', label: 'Process Issue' },
    { value: 'Quality', label: 'Quality Issue' },
    { value: 'Testing', label: 'Testing Issue' },
    { value: 'Other', label: 'Other' }
  ], []);

  // Use Fusion Manage picklist directly - show all options without filtering
  const projectOrTechnologyOptions = useMemo(() => fusionDefectTypes || [], [fusionDefectTypes]);

  const formatProjectLabel = (project) => {
    if (!project) return '';
    const id = project.ProjectID !== undefined && project.ProjectID !== null
      ? String(project.ProjectID)
      : '';
    const name = project.ProjectName ? String(project.ProjectName).trim() : '';
    return name ? `${id} - ${name}` : id;
  };

  const projectLookup = useMemo(() => {
    const map = new Map();
    (projects || []).forEach(project => {
      map.set(String(project.ProjectID), project);
    });
    return map;
  }, [projects]);

  const projectIdOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    console.log('[IPIForm] Generating projectIdOptions from', projects?.length || 0, 'projects');
    (projects || []).forEach(project => {
      const id = String(project.ProjectID);
      if (!seen.has(id)) {
        seen.add(id);
        // Store full label for dropdown display, but value is just the ID
        options.push({ 
          value: id, 
          label: formatProjectLabel(project),
          // Add project name for later use
          projectName: project.ProjectName ? String(project.ProjectName).trim() : ''
        });
      }
    });
    console.log('[IPIForm] projectIdOptions generated:', options.length, 'options');
    if (options.length > 0) {
      console.log('[IPIForm] Sample projectIdOptions:', options.slice(0, 3));
    }
    return options;
  }, [projects]);

  const projectNameOptions = useMemo(() => (projects || []).map((project) => {
    const id = String(project.ProjectID);
    const name = project.ProjectName ? String(project.ProjectName).trim() : '';
    return {
      value: id,
      label: formatProjectLabel(project), // Full string for dropdown
      projectName: name // Store name separately
    };
  }), [projects]);

  const jobLookup = useMemo(() => {
    const map = new Map();
    (jobs || []).forEach(job => {
      map.set(String(job.JobNum), job);
    });
    return map;
  }, [jobs]);

  const jobOptions = useMemo(() => (jobs || []).map((job) => {
    const jobNum = job.JobNum ? String(job.JobNum) : '';
    const description = job.JobDescription ? String(job.JobDescription).trim() : '';
    const label = description ? `${jobNum} - ${description}` : jobNum;
    return {
      value: jobNum,
      label
    };
  }), [jobs]);

  const selectedProjectId = useMemo(() => {
    const rawValue = formData.ProjectID;
    if (!rawValue) return null;
    if (typeof rawValue === 'object') {
      if (rawValue === null) return null;
      if (typeof rawValue.value !== 'undefined') return String(rawValue.value);
      return String(rawValue);
    }
    return String(rawValue);
  }, [formData.ProjectID]);

  // Epicor supplier fetching removed - only Fusion Manage suppliers from picklist are used
  // Fusion Manage validates against its picklist, so Epicor suppliers cannot be accepted

  const selectedProjectNameOption = useMemo(() => {
    if (!selectedProjectId) {
      return null;
    }
    const option = projectNameOptions.find(option => option.value === selectedProjectId) || null;
    // If option found, return it with label showing only the project name (not full string)
    if (option) {
      return {
        ...option,
        label: option.projectName || option.label // Show only project name when selected
      };
    }
    return null;
  }, [projectNameOptions, selectedProjectId]);

  useEffect(() => {
    setFormData(prev => {
      if (!selectedProjectId) {
        if (!prev.ProjectName) {
          return prev;
        }
        return { ...prev, ProjectName: '' };
      }

      const project = projectLookup.get(selectedProjectId);
      const projectName = project?.ProjectName || '';
      if (prev.ProjectName === projectName) {
        return prev;
      }
      return { ...prev, ProjectName: projectName };
    });
  }, [selectedProjectId, projectLookup, setFormData]);

  useEffect(() => {
    if (jobs.length !== 1) {
      return;
    }
    const job = jobs[0];
    setFormData(prev => {
      if (prev.JobNumber) {
        return prev;
      }
      return {
        ...prev,
        JobNumber: String(job.JobNum),
        JobCode: job.JobDescription || ''
      };
    });
  }, [jobs, setFormData]);

  const epicorGlobalSites = useMemo(() => (globalSites || []).map((site) => ({
    value: site.value,
    label: site.label,
  })), [globalSites]);
  
  const epicorSites = useMemo(() => (sites || []).map((site) => ({
    value: site.value,
    label: site.label,
  })), [sites]);

  const epicorDepartments = useMemo(() => (departments || []).map((dept) => ({
    value: dept.value,
    label: dept.label,
  })), [departments]);

  const fallbackGlobalSites = [
    { value: 'UK', label: 'United Kingdom' },
    { value: 'NZ', label: 'New Zealand' },
    { value: 'US', label: 'United States' },
    { value: 'AU', label: 'Australia' }
  ];

  const fallbackSites = [
    { value: 'MAIN', label: 'Main Site' },
    { value: 'WAREHOUSE', label: 'Warehouse' },
    { value: 'PRODUCTION', label: 'Production Floor' }
  ];

  const fallbackDepartments = [
    { value: 'QA', label: 'Quality Assurance' },
    { value: 'PRODUCTION', label: 'Production' },
    { value: 'ENGINEERING', label: 'Engineering' },
    { value: 'LOGISTICS', label: 'Logistics' },
    { value: 'ADMIN', label: 'Administration' }
  ];

  const locationOptions = useMemo(() => {
    const selectedGlobalSiteAssigned = formData.GlobalSiteAssigned?.value || formData.GlobalSiteAssigned || null;
    const selectedSiteAssigned = formData.SiteAssigned?.value || formData.SiteAssigned || null;
    const selectedGlobalSiteRaisedBy = formData.GlobalSiteRaisedBy?.value || formData.GlobalSiteRaisedBy || null;
    const selectedSiteRaisedBy = formData.SiteRaisedBy?.value || formData.SiteRaisedBy || null;

    const parsedGlobalSites = parsedLocations.globalSites.length > 0 
      ? parsedLocations.globalSites 
      : extractUniqueGlobalSites(fusionLocations);

    // Show ALL sites (no filtering by global site)
    const parsedSitesAssigned = parsedLocations.sites.length > 0
      ? parsedLocations.sites
      : extractUniqueSites(fusionLocations);

    const parsedSitesRaisedBy = parsedLocations.sites.length > 0
      ? parsedLocations.sites
      : extractUniqueSites(fusionLocations);

    const parsedDepartmentsAssigned = (selectedGlobalSiteAssigned || selectedSiteAssigned)
      ? extractUniqueDepartments(fusionLocations, selectedGlobalSiteAssigned, selectedSiteAssigned)
      : parsedLocations.departments.length > 0 
        ? parsedLocations.departments 
        : extractUniqueDepartments(fusionLocations);

    const parsedDepartmentsRaisedBy = (selectedGlobalSiteRaisedBy || selectedSiteRaisedBy)
      ? extractUniqueDepartments(fusionLocations, selectedGlobalSiteRaisedBy, selectedSiteRaisedBy)
      : parsedLocations.departments.length > 0 
        ? parsedLocations.departments 
        : extractUniqueDepartments(fusionLocations);

    return {
      selectedGlobalSiteAssigned,
      selectedSiteAssigned,
      selectedGlobalSiteRaisedBy,
      selectedSiteRaisedBy,
      parsedGlobalSites,
      parsedSitesAssigned,
      parsedSitesRaisedBy,
      parsedDepartmentsAssigned,
      parsedDepartmentsRaisedBy
    };
  }, [formData.GlobalSiteAssigned, formData.SiteAssigned, formData.GlobalSiteRaisedBy, formData.SiteRaisedBy, parsedLocations, fusionLocations]);

  const {
    selectedGlobalSiteAssigned,
    selectedSiteAssigned,
    selectedGlobalSiteRaisedBy,
    selectedSiteRaisedBy,
    parsedGlobalSites,
    parsedSitesAssigned,
    parsedSitesRaisedBy,
    parsedDepartmentsAssigned,
    parsedDepartmentsRaisedBy
  } = locationOptions;

  // Use valid options from Fusion Manage if available, otherwise fall back to parsed/Epicor data
  const globalSiteOptionsAssigned = useMemo(() => {
    if (validGlobalSiteOptionsAssigned.length > 0) {
      return validGlobalSiteOptionsAssigned;
    }
    const merged = mergePicklists(epicorGlobalSites, parsedGlobalSites);
    return merged.length === 0 ? fallbackGlobalSites : merged;
  }, [validGlobalSiteOptionsAssigned, epicorGlobalSites, parsedGlobalSites]);

  const globalSiteOptionsRaisedBy = useMemo(() => {
    if (validGlobalSiteOptionsRaisedBy.length > 0) {
      return validGlobalSiteOptionsRaisedBy;
    }
    const merged = mergePicklists(epicorGlobalSites, parsedGlobalSites);
    return merged.length === 0 ? fallbackGlobalSites : merged;
  }, [validGlobalSiteOptionsRaisedBy, epicorGlobalSites, parsedGlobalSites]);

  // For backward compatibility
  const globalSiteOptions = globalSiteOptionsAssigned;

  const siteOptionsAssigned = useMemo(() => {
    if (filteredSiteOptionsAssigned.length > 0) {
      return filteredSiteOptionsAssigned;
    }
    const merged = mergePicklists(epicorSites, parsedSitesAssigned);
    return merged.length === 0 ? fallbackSites : merged;
  }, [filteredSiteOptionsAssigned, epicorSites, parsedSitesAssigned]);

  const siteOptionsRaisedBy = useMemo(() => {
    if (filteredSiteOptionsRaisedBy.length > 0) {
      return filteredSiteOptionsRaisedBy;
    }
    const merged = mergePicklists(epicorSites, parsedSitesRaisedBy);
    return merged.length === 0 ? fallbackSites : merged;
  }, [filteredSiteOptionsRaisedBy, epicorSites, parsedSitesRaisedBy]);

  const departmentOptionsAssigned = useMemo(() => {
    if (filteredDepartmentOptionsAssigned.length > 0) {
      return filteredDepartmentOptionsAssigned;
    }
    const merged = mergePicklists(epicorDepartments, parsedDepartmentsAssigned);
    return merged.length === 0 ? fallbackDepartments : merged;
  }, [filteredDepartmentOptionsAssigned, epicorDepartments, parsedDepartmentsAssigned]);

  const departmentOptionsRaisedBy = useMemo(() => {
    if (filteredDepartmentOptionsRaisedBy.length > 0) {
      return filteredDepartmentOptionsRaisedBy;
    }
    const merged = mergePicklists(epicorDepartments, parsedDepartmentsRaisedBy);
    return merged.length === 0 ? fallbackDepartments : merged;
  }, [filteredDepartmentOptionsRaisedBy, epicorDepartments, parsedDepartmentsRaisedBy]);

  const baseChampionOptions = useMemo(() => {
    const merged = mergePicklists(
      (ncrChampions || champions || []).map((champion) => ({
        value: String(champion.value || champion.id || champion.label || ''),
        label: champion.label || champion.value || ''
      })),
      fusionNCRChampions
    );
    return merged.map(sanitizeChampionOption);
  }, [ncrChampions, champions, fusionNCRChampions]);

  const ncrChampionOptions = useMemo(() => {
    if (filteredChampionOptionsAssigned.length > 0) {
      return filteredChampionOptionsAssigned.map(sanitizeChampionOption);
    }
    if (baseChampionOptions.length === 0) {
      console.warn('[IPIForm] Both Epicor NCR Champions and Fusion CUSTOM_LOOKUP_WS_QA_NCR_CHAMPIONS are empty. Using fallback data.');
      return [
        { value: 'CHAMPION1', label: 'QA Champion' },
        { value: 'CHAMPION2', label: 'Production Champion' },
        { value: 'CHAMPION3', label: 'Engineering Champion' }
      ];
    }
    return baseChampionOptions;
  }, [filteredChampionOptionsAssigned, baseChampionOptions]);

  // Helper function to generate auto-filled fields (IPI Number, RaisedDate, IncidentDate)
  const generateAutoFields = () => {
    const generateIPINumber = () => {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const timestamp = now.getTime().toString().slice(-6);
      return `IPI-${year}${month}${day}-${timestamp}`;
    };

    setFormData(prevData => ({
      ...prevData,
      Number: generateIPINumber(),
      RaisedDate: new Date().toISOString().split('T')[0],
      IncidentDate: new Date().toISOString().split('T')[0],
      // Don't set ProjectOrTechnology by default - let user select
      ProjectOrTechnology: null
    }));
  };

  // Auto-generate IPI number and set current date on mount
  useEffect(() => {
    generateAutoFields();
  }, []);

  useEffect(() => {
    let cancelled = false;

    // Batch all initial data fetches in parallel
    const loadInitialData = async () => {
      const projectIdValue = formData.ProjectID?.value || formData.ProjectID;

      // Fetch Fusion Manage data in parallel
      setLoadingFusionData(true);
      const parsedLocationsPromise = fetch('/api/fm/locations/parsed', {
        credentials: 'include'
      })
        .then(res => res.ok ? res.json() : { success: false })
        .then(data => data.success ? data : { globalSites: [], sites: [], departments: [] })
        .catch(() => ({ globalSites: [], sites: [], departments: [] }));

      const fusionPicklistsPromise = Promise.all([
        fetchFusionManagePicklistDirect('CUSTOM_LOOKUP_DEFECT_TYPE'),
        fetchFusionManagePicklistDirect('CUSTOM_LOOKUP_WS_REASON_CODE'),
        fetchFusionManagePicklistDirect('CUSTOM_LOOKUP_WS_QA_CLOCKING_CODES'),
        fetchFusionManagePicklistDirect('CUSTOM_LOOKUP_YES_NO'),
        fetchFusionManagePicklistDirect('CUSTOM_LOOKUP_WS_QA_PRODUCT_TYPE'),
        fetchFusionManagePicklistDirect('CUSTOM_LOOKUP_IPI_ACTION_TAKEN'),
        fetchFusionManagePicklistDirect('CUSTOM_LOOKUP_WS_QA_LOCATION'),
        fetchFusionManagePicklistDirect('CUSTOM_LOOKUP_WS_QA_NCR_CHAMPIONS'),
        fetch('/api/fm/departments', { credentials: 'include' })
          .then(res => res.json())
          .then(data => data.success ? data.options : [])
          .catch(() => [])
      ]).catch(() => [[], [], [], [], [], [], [], [], []]);

      // Affected Product: use IPI workspace field options (matches what FM accepts) so options like "Elevation" are included
      const ipiAffectedProductOptionsPromise = fetch(`/api/fm/workspaces/${WORKSPACE_IDS.IPI}/views/1/fields/AFFECTED_PRODUCT/options/all`, { credentials: 'include' })
        .then(res => (res.ok ? res.json() : { success: false, options: [] }))
        .then(data => (data.success && Array.isArray(data.options) ? data.options : []))
        .catch(() => []);

      // Execute independent fetches in parallel
      const [parsedLocationsData, fusionPicklists, ipiAffectedProductOptions] = await Promise.all([
        parsedLocationsPromise,
        fusionPicklistsPromise,
        ipiAffectedProductOptionsPromise
      ]);

      if (cancelled) return;

      // Set Fusion Manage data immediately
      if (parsedLocationsData.globalSites || parsedLocationsData.sites || parsedLocationsData.departments) {
        setParsedLocations({
          globalSites: parsedLocationsData.globalSites || [],
          sites: parsedLocationsData.sites || [],
          departments: parsedLocationsData.departments || []
        });
      }

      const [ipiTypes, reasonCodes, clockingCodes, yesNo, productTypesFromPicklist, ipiActionTaken, locations, champions, departments] = fusionPicklists;

      // Use IPI workspace AFFECTED_PRODUCT field options when available (includes e.g. Elevation); else fall back to picklist
      const productTypes = (ipiAffectedProductOptions && ipiAffectedProductOptions.length > 0)
        ? ipiAffectedProductOptions
        : (productTypesFromPicklist || []);
      if (ipiAffectedProductOptions && ipiAffectedProductOptions.length > 0) {
        console.log('[IPIForm] Using Affected Product options from IPI workspace field:', ipiAffectedProductOptions.length, 'products');
      } else if (productTypesFromPicklist && productTypesFromPicklist.length > 0) {
        console.log('[IPIForm] Using Affected Product options from picklist (fallback):', productTypesFromPicklist.length, 'products');
      }
      
      // Clean defect type (reason code) values - remove trailing spaces and dashes
      const cleanDefectTypeValue = (item) => {
        if (!item) return item;
        const cleanValue = (str) => {
          if (!str || typeof str !== 'string') return str;
          return str.trim().replace(/\s+-\s*$/, '').trim(); // Remove trailing " -" or "-" with optional spaces
        };
        return {
          ...item,
          value: cleanValue(item.value),
          label: cleanValue(item.label) || cleanValue(item.value)
        };
      };
      const cleanedReasonCodes = reasonCodes.map(cleanDefectTypeValue);
      
      setFusionDefectTypes(ipiTypes);
      setFusionReasonCodes(cleanedReasonCodes);
      setFusionClockingCodes(clockingCodes);
      setFusionYesNo(yesNo);
      
      // Enhanced logging for product types
      if (productTypes && productTypes.length > 0) {
        console.log('[IPIForm] Setting fusionProductTypes from loadInitialData:', productTypes.length, 'products');
        console.log('[IPIForm] Sample product:', productTypes[0]);
        console.log('[IPIForm] All products received:', productTypes);
        console.log('[IPIForm] Product structure check:', productTypes.map(p => ({
          hasValue: !!p.value,
          hasLabel: !!p.label,
          value: p.value,
          label: p.label,
          valueType: typeof p.value,
          labelType: typeof p.label
        })));
      } else {
        console.warn('[IPIForm] No productTypes loaded from loadInitialData');
        console.warn('[IPIForm] productTypes value:', productTypes);
        console.warn('[IPIForm] productTypes type:', typeof productTypes);
        console.warn('[IPIForm] productTypes length:', productTypes?.length);
      }
      
      setFusionProductTypes(productTypes || []);
      setFusionIpiActionTaken(ipiActionTaken);
      setFusionLocations(locations);
      setFusionNCRChampions(champions);
      setFusionDepartments(departments);
      setLoadingFusionData(false);
    };

    loadInitialData();

    // Cleanup function to cancel async operations
    return () => {
      cancelled = true;
    };
  }, [formData.ProjectID]);

  // Fetch projects when ProjectOrTechnology is "Project"
  useEffect(() => {
    let cancelled = false;
    const rawValue = formData.ProjectOrTechnology;
    const selectedType = rawValue?.value || rawValue?.label || rawValue;
    const normalizedType = selectedType ? String(selectedType).trim().toLowerCase() : '';
    const ipiTypeNormalized = formData.IPIType ? String(formData.IPIType).trim().toLowerCase() : '';
    
    // Check if it's a project type (case-insensitive, handles variations)
    const isProjectType = normalizedType === 'project' || 
                         normalizedType.includes('project') ||
                         ipiTypeNormalized === 'project' ||
                         (rawValue?.value && String(rawValue.value).toLowerCase().includes('project')) ||
                         (rawValue?.label && String(rawValue.label).toLowerCase().includes('project'));
    
    console.log('[IPIForm] ProjectOrTechnology changed:', {
      raw: rawValue,
      selectedType,
      normalizedType,
      ipiType: formData.IPIType,
      ipiTypeNormalized,
      isProjectType,
      willFetch: isProjectType
    });
    
    if (!isProjectType) {
      console.log('[IPIForm] ProjectOrTechnology is not a project type, clearing projects. Current value:', normalizedType);
      setProjects([]);
      setJobs([]);
      return;
    }
    
    console.log('[IPIForm] ProjectOrTechnology is a project type, will fetch projects from Epicor');

    const fetchProjects = async () => {
      setLoadingProjects(true);
      try {
        // Fetch projects from all companies - no company filter to allow cross-company project access
        console.log('[IPIForm] Fetching projects from Epicor (all companies)');
        const response = await fetchGraphQL(NCRPROJECTLIST_LOOKUP_QUERY, {
          company: null // Pass null to get projects from all companies
        });
        
        if (cancelled) return;
        
        console.log('[IPIForm] Received response from Epicor:', response);
        
        // Extract projects from response
        const allProjects = [];
        if (response && response.ncrProjectList) {
          console.log(`[IPIForm] Found ${response.ncrProjectList.length} projects`);
          allProjects.push(...response.ncrProjectList);
        } else {
          console.warn('[IPIForm] No projects found in response:', response);
        }
        
        console.log('[IPIForm] Total projects:', allProjects.length);
        if (allProjects.length > 0) {
          console.log('[IPIForm] Sample projects:', allProjects.slice(0, 3));
        }
        
        if (!cancelled) {
          setProjects(allProjects);
          console.log('[IPIForm] Projects state updated with', allProjects.length, 'projects');
        }
      } catch (error) {
        console.error('[IPIForm] Error fetching projects:', error);
      } finally {
        if (!cancelled) {
          setLoadingProjects(false);
        }
      }
    };

    fetchProjects();
    return () => { cancelled = true; };
  }, [formData.ProjectOrTechnology, formData.IPIType, fetchGraphQL]);

  // Fetch products when ProjectOrTechnology is "Product" or "Technology"
  // Note: IPIForm uses fusionProductTypes (loaded in loadInitialData) for productTypeOptions
  // This useEffect ensures productNames state is also set for consistency
  useEffect(() => {
    let cancelled = false;
    const selectedType = formData.ProjectOrTechnology?.value || formData.ProjectOrTechnology?.label || formData.ProjectOrTechnology;
    const ipiType = formData.IPIType?.value || formData.IPIType?.label || formData.IPIType;
    
    if (selectedType !== 'Product' && selectedType !== 'Technology' && ipiType !== 'Product' && ipiType !== 'Technology') {
      setProductNames([]);
      return;
    }

    // Products are already loaded in fusionProductTypes from loadInitialData
    // Set productNames state to match fusionProductTypes for consistency
    if (!cancelled && fusionProductTypes && fusionProductTypes.length > 0) {
      setProductNames(fusionProductTypes);
    }
    
    return () => { cancelled = true; };
  }, [formData.ProjectOrTechnology, formData.IPIType, fusionProductTypes]);

  // Fetch jobs when ProjectID is selected
  useEffect(() => {
    let cancelled = false;
    const projectId = selectedProjectId;
    
    if (!projectId) {
      setJobs([]);
      return;
    }

    const fetchJobs = async () => {
      setLoadingJobs(true);
      try {
        // Fetch jobs from all companies - no company filter to allow cross-company job access
        console.log('[IPIForm] Fetching jobs from Epicor for project:', projectId, '(all companies)');
        const response = await fetchGraphQL(NCRJOBLIST_LOOKUP_QUERY, { 
          projectID: String(projectId),
          company: null // Pass null to get jobs from all companies
        });
        
        if (cancelled) {
          console.log('[IPIForm] Job fetch was cancelled');
          return;
        }
        
        console.log('[IPIForm] Received jobs response from Epicor:', response);
        
        // Extract jobs from response
        const allJobs = [];
        if (response && response.ncrJobList) {
          console.log(`[IPIForm] Found ${response.ncrJobList.length} jobs`);
          allJobs.push(...response.ncrJobList);
        } else {
          console.warn('[IPIForm] No jobs found in response:', response);
        }
        
        console.log('[IPIForm] Total jobs:', allJobs.length);
        if (allJobs.length > 0) {
          console.log('[IPIForm] Sample jobs:', allJobs.slice(0, 3));
        }
        
        if (!cancelled) {
          setJobs(allJobs);
        }
      } catch (error) {
        console.error('[IPIForm] Error fetching jobs:', error);
        console.error('[IPIForm] Error stack:', error.stack);
      } finally {
        if (!cancelled) {
          setLoadingJobs(false);
        }
      }
    };

    fetchJobs();
    return () => { cancelled = true; };
  }, [selectedProjectId, fetchGraphQL]);

  // Fetch valid Global Site options for Assigned and Raised By sections on mount
  useEffect(() => {
    let cancelled = false;
    const fetchGlobalSiteOptions = async () => {
      setLoadingGlobalSiteOptions(true);
      try {
        // Fetch valid options for GLOBAL_SITE_ASSIGNED (Assigned section)
        const assignedResponse = await fetch(`/api/fm/workspaces/${WORKSPACE_IDS.NCR}/views/1/fields/GLOBAL_SITE_ASSIGNED/options`);
        if (cancelled) return;
        
        if (assignedResponse.ok) {
          const assignedData = await assignedResponse.json();
          if (assignedData.success && assignedData.options) {
            const assignedOptions = assignedData.options.map(opt => ({
              value: opt.value || opt.label,
              label: opt.label || opt.value
            }));
            if (!cancelled) {
              setValidGlobalSiteOptionsAssigned(assignedOptions);
              console.log(`[IPIForm] Fetched ${assignedOptions.length} valid GLOBAL_SITE_ASSIGNED options:`, assignedOptions.map(o => o.label).join(', '));
            }
          }
        }

        // Fetch valid options for GLOBAL_SITE (Raised By section)
        const raisedByResponse = await fetch(`/api/fm/workspaces/${WORKSPACE_IDS.NCR}/views/1/fields/GLOBAL_SITE/options`);
        if (cancelled) return;
        
        if (raisedByResponse.ok) {
          const raisedByData = await raisedByResponse.json();
          if (raisedByData.success && raisedByData.options) {
            const raisedByOptions = raisedByData.options.map(opt => ({
              value: opt.value || opt.label,
              label: opt.label || opt.value
            }));
            if (!cancelled) {
              setValidGlobalSiteOptionsRaisedBy(raisedByOptions);
              console.log(`[IPIForm] Fetched ${raisedByOptions.length} valid GLOBAL_SITE (Raised By) options:`, raisedByOptions.map(o => o.label).join(', '));
            }
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error('[IPIForm] Error fetching valid Global Site options:', error);
        }
      } finally {
        if (!cancelled) {
          setLoadingGlobalSiteOptions(false);
        }
      }
    };

    fetchGlobalSiteOptions();
    return () => { cancelled = true; };
  }, []);

  // Debounced values for filtered option fetches
  const debouncedGlobalSiteAssigned = useDebounce(formData.GlobalSiteAssigned, 300);
  const debouncedSiteAssigned = useDebounce(formData.SiteAssigned, 300);
  const debouncedDepartmentAssigned = useDebounce(formData.DepartmentAssigned, 300);
  const debouncedGlobalSiteRaisedBy = useDebounce(formData.GlobalSiteRaisedBy, 300);
  const debouncedSiteRaisedBy = useDebounce(formData.SiteRaisedBy, 300);

  // Fetch filtered SITE options for assigned section when GLOBAL_SITE changes (with debouncing)
  useEffect(() => {
    let cancelled = false;
    const fetchSiteOptionsAssigned = async () => {
      const globalSite = debouncedGlobalSiteAssigned?.value || debouncedGlobalSiteAssigned;
      if (!globalSite) {
        setFilteredSiteOptionsAssigned([]);
        return;
      }

      setLoadingFilteredOptions(true);
      try {
        const queryParams = new URLSearchParams({ GLOBAL_SITE_ASSIGNED: globalSite });
        const response = await fetch(`/api/fm/workspaces/${WORKSPACE_IDS.NCR}/views/1/fields/SITE_ASSIGNED/options?${queryParams.toString()}`);
        if (cancelled) return;
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.options) {
            const options = data.options.map(opt => ({
              value: String(opt.value || opt.label),
              label: opt.label || opt.value
            }));
            if (!cancelled) {
              setFilteredSiteOptionsAssigned(options);
              console.log(`[IPIForm] Fetched ${options.length} filtered SITE_ASSIGNED options for GLOBAL_SITE_ASSIGNED: ${globalSite}`);
            }
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error('[IPIForm] Error fetching filtered SITE_ASSIGNED options:', error);
          setFilteredSiteOptionsAssigned([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingFilteredOptions(false);
        }
      }
    };

    fetchSiteOptionsAssigned();
    return () => { cancelled = true; };
  }, [debouncedGlobalSiteAssigned]);

  // Fetch filtered DEPARTMENT options for assigned section when site changes (with debouncing)
  useEffect(() => {
    let cancelled = false;
    const fetchDepartmentOptionsAssigned = async () => {
      const globalSite = debouncedGlobalSiteAssigned?.value || debouncedGlobalSiteAssigned;
      const site = debouncedSiteAssigned?.value || debouncedSiteAssigned;
      if (!globalSite || !site) {
        setFilteredDepartmentOptionsAssigned([]);
        return;
      }

      setLoadingFilteredOptions(true);
      try {
        const queryParams = new URLSearchParams({
          GLOBAL_SITE_ASSIGNED: globalSite,
          SITE_ASSIGNED: site
        });
        const response = await fetch(`/api/fm/workspaces/${WORKSPACE_IDS.NCR}/views/1/fields/DEPARTMENT_ASSIGNED/options?${queryParams.toString()}`);
        if (cancelled) return;
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.options) {
            const options = data.options.map(opt => ({
              value: String(opt.value || opt.label),
              label: opt.label || opt.value
            }));
            if (!cancelled) {
              setFilteredDepartmentOptionsAssigned(options);
              console.log(`[IPIForm] Fetched ${options.length} filtered DEPARTMENT_ASSIGNED options`);
            }
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error('[IPIForm] Error fetching filtered DEPARTMENT_ASSIGNED options:', error);
          setFilteredDepartmentOptionsAssigned([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingFilteredOptions(false);
        }
      }
    };

    fetchDepartmentOptionsAssigned();
    return () => { cancelled = true; };
  }, [debouncedGlobalSiteAssigned, debouncedSiteAssigned]);

  // Auto-derive GlobalSiteAssigned when SiteAssigned changes
  useEffect(() => {
    const selectedSite = formData.SiteAssigned?.value || formData.SiteAssigned;
    if (!selectedSite) {
      return; // Don't clear global site if user is just changing selection
    }

    const allSites = parsedLocations.sites.length > 0
      ? parsedLocations.sites
      : extractUniqueSites(fusionLocations);

    const derivedGlobalSite = deriveGlobalSiteFromSite(allSites, selectedSite);

    if (derivedGlobalSite) {
      setFormData(prev => ({
        ...prev,
        GlobalSiteAssigned: derivedGlobalSite
      }));
    }
  }, [formData.SiteAssigned, parsedLocations.sites, fusionLocations]);

  // Auto-derive GlobalSiteRaisedBy when SiteRaisedBy changes
  useEffect(() => {
    const selectedSite = formData.SiteRaisedBy?.value || formData.SiteRaisedBy;
    if (!selectedSite) {
      return;
    }

    const allSites = parsedLocations.sites.length > 0
      ? parsedLocations.sites
      : extractUniqueSites(fusionLocations);

    const derivedGlobalSite = deriveGlobalSiteFromSite(allSites, selectedSite);

    if (derivedGlobalSite) {
      setFormData(prev => ({
        ...prev,
        GlobalSiteRaisedBy: derivedGlobalSite
      }));
    }
  }, [formData.SiteRaisedBy, parsedLocations.sites, fusionLocations]);

  // Fetch filtered NCR_CHAMPION_ASSIGNED options when global/site/department change (with debouncing)
  useEffect(() => {
    let cancelled = false;
    const fetchChampionOptionsAssigned = async () => {
      const globalSite = debouncedGlobalSiteAssigned?.value || debouncedGlobalSiteAssigned;
      const site = debouncedSiteAssigned?.value || debouncedSiteAssigned;
      const department = debouncedDepartmentAssigned?.value || debouncedDepartmentAssigned;
      if (!globalSite || !site || !department) {
        setFilteredChampionOptionsAssigned([]);
        return;
      }

      setLoadingFilteredOptions(true);
      try {
        const queryParams = new URLSearchParams({
          GLOBAL_SITE_ASSIGNED: globalSite,
          SITE_ASSIGNED: site,
          DEPARTMENT_ASSIGNED: department
        });
        const response = await fetch(`/api/fm/workspaces/${WORKSPACE_IDS.NCR}/views/1/fields/NCR_CHAMPION_ASSIGNED/options?${queryParams.toString()}`);
        if (cancelled) return;
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.options) {
            const options = data.options.map(opt => sanitizeChampionOption({
              value: opt.value || opt.id || opt.label,
              label: opt.label || opt.value
            }));
            if (!cancelled) {
              setFilteredChampionOptionsAssigned(options);
              console.log(`[IPIForm] Fetched ${options.length} filtered NCR_CHAMPION_ASSIGNED options`);
            }
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error('[IPIForm] Error fetching filtered NCR_CHAMPION_ASSIGNED options:', error);
          setFilteredChampionOptionsAssigned([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingFilteredOptions(false);
        }
      }
    };

    fetchChampionOptionsAssigned();
    return () => { cancelled = true; };
  }, [debouncedGlobalSiteAssigned, debouncedSiteAssigned, debouncedDepartmentAssigned]);

  // Fetch filtered SITE options for Raised By when GLOBAL_SITE changes (with debouncing)
  useEffect(() => {
    let cancelled = false;
    const fetchSiteOptionsRaisedBy = async () => {
      const globalSite = debouncedGlobalSiteRaisedBy?.value || debouncedGlobalSiteRaisedBy;
      if (!globalSite) {
        setFilteredSiteOptionsRaisedBy([]);
        return;
      }

      setLoadingFilteredOptions(true);
      try {
        const queryParams = new URLSearchParams({ GLOBAL_SITE: globalSite });
        const response = await fetch(`/api/fm/workspaces/${WORKSPACE_IDS.NCR}/views/1/fields/SITE/options?${queryParams.toString()}`);
        if (cancelled) return;
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.options) {
            const options = data.options.map(opt => ({
              value: String(opt.value || opt.label),
              label: opt.label || opt.value
            }));
            if (!cancelled) {
              setFilteredSiteOptionsRaisedBy(options);
              console.log(`[IPIForm] Fetched ${options.length} filtered SITE (Raised By) options`);
            }
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error('[IPIForm] Error fetching filtered SITE (Raised By) options:', error);
          setFilteredSiteOptionsRaisedBy([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingFilteredOptions(false);
        }
      }
    };

    fetchSiteOptionsRaisedBy();
    return () => { cancelled = true; };
  }, [debouncedGlobalSiteRaisedBy]);

  // Fetch filtered DEPARTMENT options for Raised By when site changes (with debouncing)
  useEffect(() => {
    let cancelled = false;
    const fetchDepartmentOptionsRaisedBy = async () => {
      const globalSite = debouncedGlobalSiteRaisedBy?.value || debouncedGlobalSiteRaisedBy;
      const site = debouncedSiteRaisedBy?.value || debouncedSiteRaisedBy;
      if (!globalSite) {
        setFilteredDepartmentOptionsRaisedBy([]);
        return;
      }

      setLoadingFilteredOptions(true);
      try {
        const queryParams = new URLSearchParams({
          GLOBAL_SITE: globalSite
        });
        if (site) {
          queryParams.append('SITE', site);
        }
        const response = await fetch(`/api/fm/workspaces/${WORKSPACE_IDS.NCR}/views/1/fields/DEPARTMENT/options?${queryParams.toString()}`);
        if (cancelled) return;
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.options) {
            const options = data.options.map(opt => ({
              value: String(opt.value || opt.label),
              label: opt.label || opt.value
            }));
            if (!cancelled) {
              setFilteredDepartmentOptionsRaisedBy(options);
              console.log(`[IPIForm] Fetched ${options.length} filtered DEPARTMENT (Raised By) options`);
            }
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error('[IPIForm] Error fetching filtered DEPARTMENT (Raised By) options:', error);
          setFilteredDepartmentOptionsRaisedBy([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingFilteredOptions(false);
        }
      }
    };

    fetchDepartmentOptionsRaisedBy();
    return () => { cancelled = true; };
  }, [debouncedGlobalSiteRaisedBy, debouncedSiteRaisedBy]);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setHasInteracted(true);
    // ALWAYS use functional setState to prevent race conditions and lost updates
    setFormData(prevFormData => ({
      ...prevFormData,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);

  const handleIPITypeChange = useCallback((selectedOption) => {
    setHasInteracted(true);
    // Handle null/cleared selection
    if (!selectedOption) {
      setFormData(prev => ({
        ...prev,
        IPIType: null,
        ProjectOrTechnology: null,
        ProjectID: null,
        ProjectName: null,
        JobCode: '',
        JobNumber: null,
        IsReworkRequired: null,
        AffectedProduct: null,
        IsPCBRelated: null,
        PCBSerialNumber: '',
        IssueType: null
      }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      IPIType: selectedOption.value,
      ProjectOrTechnology: selectedOption.value,
      ProjectID: null,
      ProjectName: null,
      JobCode: '',
      JobNumber: null,
      IsReworkRequired: null,
      AffectedProduct: null,
      IsPCBRelated: null,
      PCBSerialNumber: '',
      IssueType: null
    }));
  }, []);

  const handleSelectChange = useCallback((selectedOption, field) => {
    setHasInteracted(true);
    console.log(`[IPIForm handleSelectChange] Field: ${field}, Value:`, selectedOption);

    const getOptionValue = (option) => {
      if (option === null || option === undefined) {
        return null;
      }
      if (typeof option === 'object' && !Array.isArray(option)) {
        return option.value ?? option.label ?? option.id ?? null;
      }
      return option;
    };

    // ALWAYS use functional setState to prevent race conditions and lost updates
    setFormData(prevFormData => {
      if (field === 'ProjectID') {
        // Parse ProjectID and ProjectName from the label (e.g., "25W0159 - Jelly Roll UK EU 2025")
        const label = selectedOption?.label || '';
        const projectId = parseProjectIdFromLabel(label); // Extract "25W0159" from label
        const projectName = parseProjectNameFromLabel(label); // Extract "Jelly Roll UK EU 2025" from label
        setJobs([]);
        return {
          ...prevFormData,
          ProjectID: projectId, // Store parsed project ID (e.g., "25W0159")
          ProjectName: projectName, // Store parsed project name (e.g., "Jelly Roll UK EU 2025")
          JobNumber: null,
          JobCode: ''
        };
      }

      if (field === 'ProjectName') {
        // Use the option's value directly (it's already the project ID as a string)
        const projectId = selectedOption?.value || '';
        const label = selectedOption?.label || '';
        const projectName = parseProjectNameFromLabel(label);
        setJobs([]);
        return {
          ...prevFormData,
          ProjectID: projectId, // Store the value directly from the option
          ProjectName: projectName,
          JobNumber: null,
          JobCode: ''
        };
      }

      if (field === 'JobNumber') {
        const jobValue = getOptionValue(selectedOption);
        const job = jobLookup.get(String(jobValue ?? ''));
        return {
          ...prevFormData,
          JobNumber: jobValue,
          JobCode: job ? job.JobDescription || '' : ''
        };
      }

      if (field === 'NCRChampionAssigned') {
        const championValue = getOptionValue(selectedOption);
        // Extract QAEmail from the champion object when champion is selected
        const qaEmail = selectedOption?.email 
          || selectedOption?.QA_EMAIL 
          || selectedOption?.qa_email
          || selectedOption?.rawItem?.QA_EMAIL
          || selectedOption?.rawItem?.qa_email
          || null;
        
        console.log(`[handleSelectChange] NCRChampionAssigned selected. Extracted QAEmail:`, qaEmail);
        
        return {
          ...prevFormData,
          NCRChampionAssigned: championValue,
          QAEmail: qaEmail
        };
      }

      if (field === 'ProjectOrTechnology' || field === 'IPIType') {
        // When ProjectOrTechnology or IPIType changes, clear fields that are no longer relevant
        const newType = getOptionValue(selectedOption) || selectedOption?.label || selectedOption;
        const newFormData = {
          ...prevFormData,
          [field]: selectedOption,
        };
        
        // If switching TO "Project": Clear Product/Technology-related fields
        if (newType === 'Project') {
          newFormData.AffectedProduct = null;
          newFormData.AffectedVariant = null;
          newFormData.SoftwareVersion = '';
          newFormData.ProductSerialNumber = '';
          newFormData.AffectedQuantity = '';
          setProductNames([]);
        }
        // If switching TO "Product" or "Technology": Clear Project-related fields
        else if (newType === 'Product' || newType === 'Technology') {
          newFormData.ProjectID = null;
          newFormData.ProjectName = null;
          newFormData.JobNumber = null;
          newFormData.JobCode = '';
          newFormData.DrawingNumber = '';
          setProjects([]);
          setJobs([]);
        }
        // If switching to something else: Clear both Project and Product fields
        else {
          newFormData.ProjectID = null;
          newFormData.ProjectName = null;
          newFormData.JobNumber = null;
          newFormData.JobCode = '';
          newFormData.DrawingNumber = '';
          newFormData.AffectedProduct = null;
          newFormData.AffectedVariant = null;
          newFormData.SoftwareVersion = '';
          newFormData.ProductSerialNumber = '';
          newFormData.AffectedQuantity = '';
          setProjects([]);
          setJobs([]);
          setProductNames([]);
        }
        
        return newFormData;
      }

      // For all other fields, just update the specific field
      return {
        ...prevFormData,
        [field]: selectedOption,
      };
    });
  }, [projectLookup, jobLookup]);

  const getMissingRequiredFields = useCallback((data) => {
    const missing = [];
    
    // Required fields regardless of Project or Technology IPI
    if (!data.ProjectOrTechnology) missing.push('Project or Technology');
    if (!data.Description) missing.push('Description');
    if (!data.IncidentDate) missing.push('Incident Date');
    if (!data.GlobalSiteAssigned) missing.push('Global Site Assigned');
    if (!data.SiteAssigned) missing.push('Local Assigned Location');
    if (!data.DepartmentAssigned) missing.push('Department Assigned');
    if (!data.GlobalSiteRaisedBy) missing.push('Global Site Raised By');
    if (!data.SiteRaisedBy) missing.push('Local Site Raise By');
    if (!data.DepartmentRaisedBy) missing.push('Department Raised By');
    if (!data.RaisedBy) missing.push('Raised By');
    if (!data.RaisedDate) missing.push('Raised Date');

    // IPI-specific validations
    if (!data.DefectType) missing.push('Defect Type');
    if (!data.PartNumber) missing.push('Part Number');
    if (!data.AffectedQuantity) missing.push('Affected Quantity');
    if (!data.ClockingCode || (typeof data.ClockingCode === 'string' && data.ClockingCode.trim() === '')) {
      missing.push('Clocking Code');
    }
    
    // IPI Type (DEFECT_TYPE) - required for all IPI forms
    const ipiType = data.IPIType?.value || data.IPIType?.label || data.IPIType || data.ProjectOrTechnology?.value || data.ProjectOrTechnology?.label || data.ProjectOrTechnology;
    if (!ipiType) missing.push('IPI Type');

    // Project-related validation
    const selectedType = ipiType || (data.ProjectOrTechnology?.value || data.ProjectOrTechnology?.label || data.ProjectOrTechnology);
    if (selectedType === 'Project') {
      if (!data.ProjectID) missing.push('Project ID (Project IPI)');
      if (!data.ProjectName) missing.push('Project Name (Project IPI)');
      if (!data.IsReworkRequired) missing.push('Is Rework Required (Project IPI)');
    }

    // Technology/Product-related validation
    if (selectedType === 'Technology' || selectedType === 'Product') {
      if (!data.AffectedProduct) missing.push('Affected Product (Technology IPI)');
      if (!data.IsPCBRelated) missing.push('Is PCB Related (Technology IPI)');
      if (!data.IssueType) missing.push('Issue Type (Technology IPI)');
      const isPcbRelatedValue = data.IsPCBRelated?.value || data.IsPCBRelated;
      if (isPcbRelatedValue === 'Yes' && !data.PCBSerialNumber) {
        missing.push('PCB Serial Number (Technology IPI)');
      }
    }

    return missing;
  }, []);

  // Wrapper for success callback that also resets the form
  const handleFormSubmitSuccess = useCallback((ipiNumber) => {
    // Reset form data
    setFormData({
      ...initialState,
      RaisedDate: new Date().toISOString().split('T')[0]
    });
    // Call original success callback
    onFormSubmitSuccess(ipiNumber);
  }, [onFormSubmitSuccess]);

  // Use shared form submission hook (called after getMissingRequiredFields is defined)
  const {
    handleSubmit: handleFormSubmit,
    isSubmitting,
    errorMessage,
    showErrorAlert,
    setShowErrorAlert,
    hdCaseNum,
    setHdCaseNum,
    fusionManageStatus: hookFusionManageStatus,
    fusionManageMessage: hookFusionManageMessage,
    hasInteracted,
    setHasInteracted,
    validationAttempted,
    setValidationAttempted,
    missingRequiredFields,
    setMissingRequiredFields
  } = useFormSubmission(
    'IPI',
    getMissingRequiredFields,
    initialState,
    handleFormSubmitSuccess,
    onFormSubmitError
  );
  
  // Only compute canSubmit based on current formData, but don't show alert until submit is clicked
  const canSubmit = useMemo(() => {
    const missing = getMissingRequiredFields(formData);
    return missing.length === 0;
  }, [formData, getMissingRequiredFields]);
  
  const shouldShowMissingAlert = validationAttempted && missingRequiredFields.length > 0;

  // Use the hook's handleSubmit directly - it handles validation, submission, and callbacks
  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    handleFormSubmit(formData);
  };
  
  // Handle Fusion Manage status updates
  useEffect(() => {
    if (hdCaseNum) {
      // Status is handled by the hook, but we can update local state if needed
      // The hook's fusionManageStatus and fusionManageMessage are available if needed
    }
  }, [hdCaseNum]);

  return (
    <div className="ipi-form-container">
      {shouldShowMissingAlert && (
        <Alert bsStyle="warning">
          <strong>Required fields missing:</strong>
          <ul>
            {missingRequiredFields.map(field => (
              <li key={field}>{field}</li>
            ))}
          </ul>
        </Alert>
      )}

      {hdCaseNum && (
        <Alert
          bsStyle="success"
          onDismiss={() => setHdCaseNum(null)}
        >
          {`IPI ${hdCaseNum} created successfully in Fusion Manage.`}
        </Alert>
      )}

      {showErrorAlert && (
        <Alert
          bsStyle="danger"
          onDismiss={() => setShowErrorAlert(false)}
        >
          {errorMessage}
        </Alert>
      )}

      <div className="ipi-form">
        <div>
          {/* Project or Product selection */}
          <label htmlFor="ProjectOrTechnology">
            <span className="ncr-required">
              <span className="text-danger">* </span>Project or Product:
            </span>
            <InfoTooltip message="Is this IPI technology or Project related?" />
          </label>
          <ReactSelect
            id="ProjectOrTechnology"
            name="ProjectOrTechnology"
            value={projectOrTechnologyOptions.find(option => option.value === (formData.ProjectOrTechnology?.value || formData.ProjectOrTechnology)) || null}
            required
            isClearable={true}
            isLoading={loadingFusionData}
            onChange={(selectedOption) => {
              handleIPITypeChange(selectedOption);
              handleSelectChange(selectedOption, 'ProjectOrTechnology');
            }}
            options={projectOrTechnologyOptions}
            placeholder="Select Project or Product"
          />

          {/* Affected Quantity */}
          <label htmlFor="AffectedQuantity">
            <span className="ncr-required">
              <span className="text-danger">* </span>Affected Quantity:
            </span>
          </label>
          <input
            type="number"
            id="AffectedQuantity"
            name="AffectedQuantity"
            value={formData.AffectedQuantity}
            onChange={handleChange}
            placeholder="Enter Quantity"
            min="1"
            required
          />

          {/* Description */}
          <label htmlFor="Description">
            <span className="ncr-required">
              <span className="text-danger">* </span>Description:
            </span>
            <InfoTooltip message="As much detail as possible" />
          </label>
          <textarea
            id="Description"
            name="Description"
            value={formData.Description}
            onChange={handleChange}
            placeholder="Describe the issue in detail"
            rows={3}
            required
          />

          {/* Defect Type */}
          <label htmlFor="DefectType">
            <span className="ncr-required">
              <span className="text-danger">* </span>Defect Type:
            </span>
          </label>
          <ReactSelect
            id="DefectType"
            name="DefectType"
            value={reasonCodeOptions.find(option => option.value === (formData.DefectType?.value || formData.DefectType)) || null}
            isLoading={loadingFusionData}
            onChange={(selectedOption) => handleSelectChange(selectedOption, 'DefectType')}
            options={reasonCodeOptions}
            placeholder="Select Defect Type"
            required
          />

          {/* Clocking Code */}
          <label htmlFor="ClockingCode">
            <span className="ncr-required">
              <span className="text-danger">* </span>Clocking Code:
            </span>
          </label>
          <ReactSelect
            id="ClockingCode"
            name="ClockingCode"
            value={clockingCodeOptions.find(option => option.value === (formData.ClockingCode?.value || formData.ClockingCode)) || null}
            isLoading={loadingFusionData}
            onChange={(selectedOption) => handleSelectChange(selectedOption, 'ClockingCode')}
            options={clockingCodeOptions}
            placeholder="Select Clocking Code"
            required
          />

          {/* Incident Date */}
          <label htmlFor="IncidentDate">
            <span className="ncr-required">
              <span className="text-danger">* </span>Incident Date:
            </span>
          </label>
          <input type="date" id="IncidentDate" name="IncidentDate" value={formData.IncidentDate} onChange={handleChange} required />

          {/* Project IPI - only show if Project is selected */}
          {(formData.ProjectOrTechnology === 'Project' || formData.ProjectOrTechnology?.value === 'Project' || formData.ProjectOrTechnology?.label === 'Project' || formData.IPIType === 'Project') && (
            <>
              <h4>Project IPI</h4>
              
              {/* Project Name */}
              <label htmlFor="ProjectName">
                <span className="ncr-required">
                  <span className="text-danger">* </span>Project Name:
                </span>
                <InfoTooltip message="What is the Project ID?&#10;What is the Project Name?" />
              </label>
              <ReactSelect
                id="ProjectName"
                name="ProjectName"
                value={selectedProjectNameOption}
                required
                isLoading={loadingProjects}
                onChange={(selectedOption) =>
                  handleSelectChange(selectedOption, 'ProjectName')
                }
                options={projectNameOptions}
                isDisabled={projectNameOptions.length === 0}
                placeholder={loadingProjects ? 'Loading projects from Epicor...' : (projectNameOptions.length === 0 ? 'No Epicor projects available' : 'Select a Project Name')}
                noOptionsMessage={() => 'No Epicor projects match your search'}
              />
              {!projectNameOptions.length && (
                <div className="text-muted small">No Epicor projects available.</div>
              )}

              {/* Job Number */}
              <label htmlFor="JobNumber">
                Job Number:
              </label>
              <ReactSelect
                id="JobNumber"
                name="JobNumber"
                value={jobOptions.find(option => option.value === String(formData.JobNumber ?? '')) || null}
                isLoading={loadingJobs}
                onChange={(selectedOption) =>
                  handleSelectChange(selectedOption, 'JobNumber')
                }
                options={jobOptions}
                isDisabled={!selectedProjectId || jobOptions.length === 0}
                placeholder={
                  loadingJobs
                    ? 'Loading jobs from Epicor...'
                    : !selectedProjectId
                      ? 'Select a Project first'
                      : jobOptions.length === 0
                        ? 'No jobs available for this project'
                        : 'Select a Job Number'
                }
                noOptionsMessage={() =>
                  !selectedProjectId
                    ? 'Select a Project to load Epicor job numbers'
                    : 'No Epicor job numbers match this project'
                }
              />
              <div className="text-muted small">
                {!selectedProjectId && 'Choose a Project Name to load Epicor job numbers.'}
                {selectedProjectId && jobOptions.length === 0 && 'Epicor returned no job numbers for this project.'}
                {selectedProjectId && jobOptions.length === 1 && 'Only one Epicor job number is available; it will be auto-selected.'}
                {selectedProjectId && jobOptions.length > 1 && `Epicor returned ${jobOptions.length} job numbers for this project.`}
              </div>

              {/* Job Code (fallback) */}
              {!formData.JobNumber && (
                <label htmlFor="JobCode">
                  Job Code:
                </label>
              )}
              {!formData.JobNumber && (
                <input
                  type="text"
                  id="JobCode"
                  name="JobCode"
                  value={formData.JobCode}
                  onChange={handleChange}
                  placeholder="Enter Job Code"
                />
              )}

              {/* Part Number */}
              <label htmlFor="PartNumber">
                <span className="ncr-required">
                  <span className="text-danger">* </span>Part Number:
                </span>
              </label>
              <input
                type="text"
                id="PartNumber"
                name="PartNumber"
                value={formData.PartNumber}
                onChange={handleChange}
                placeholder="Enter Part Number"
                required
              />

              <label htmlFor="IsReworkRequired">
                <span className="ncr-required">
                  <span className="text-danger">* </span>Is Rework Required:
                </span>
                <InfoTooltip message="Only select 'Yes' if rework needs to be pushed to production control" />
              </label>
              <ReactSelect
                id="IsReworkRequired"
                name="IsReworkRequired"
                value={reworkOptions.find(option => option.value === (formData.IsReworkRequired?.value || formData.IsReworkRequired)) || null}
                isLoading={loadingFusionData}
                onChange={(selectedOption) => handleSelectChange(selectedOption, 'IsReworkRequired')}
                options={reworkOptions}
                placeholder="Select Rework Required"
              />
            </>
          )}

          {/* Product IPI - only show if Product is selected */}
          {(formData.ProjectOrTechnology === 'Product' || formData.ProjectOrTechnology?.value === 'Product' || formData.ProjectOrTechnology?.label === 'Product' || formData.IPIType === 'Product' || formData.IPIType === 'Technology') && (
            <>
              <h4>Product IPI</h4>
              <label htmlFor="AffectedProduct">
                <span className="ncr-required">
                  <span className="text-danger">* </span>Affected Product:
                </span>
              </label>
              <ReactSelect
                key={`product-select-${productTypeOptions.length}`}
                id="AffectedProduct"
                name="AffectedProduct"
                value={productTypeOptions.find(option => option.value === (formData.AffectedProduct?.value || formData.AffectedProduct)) || null}
                isLoading={loadingFusionData}
                onChange={(selectedOption) => handleSelectChange(selectedOption, 'AffectedProduct')}
                options={productTypeOptions}
                placeholder={loadingFusionData ? "Loading products from Fusion Manage..." : (productTypeOptions.length === 0 ? "No products available" : "Select Affected Product")}
                noOptionsMessage={() => productTypeOptions.length === 0 ? "No products available. Please check console for errors." : "No matching products"}
                required
              />

              {/* Part Number */}
              <label htmlFor="PartNumber">
                <span className="ncr-required">
                  <span className="text-danger">* </span>Part Number:
                </span>
              </label>
              <input
                type="text"
                id="PartNumber"
                name="PartNumber"
                value={formData.PartNumber}
                onChange={handleChange}
                placeholder="Enter Part Number"
                required
              />

              <label htmlFor="IsPCBRelated">
                <span className="ncr-required">
                  <span className="text-danger">* </span>Is this PCB Related:
                </span>
              </label>
              <ReactSelect
                id="IsPCBRelated"
                name="IsPCBRelated"
                value={yesNoOptions.find(option => option.value === (formData.IsPCBRelated?.value || formData.IsPCBRelated)) || null}
                isLoading={loadingFusionData}
                onChange={(selectedOption) => handleSelectChange(selectedOption, 'IsPCBRelated')}
                options={yesNoOptions}
                placeholder="Select PCB Related"
              />

              <label htmlFor="PCBSerialNumber">
                <span className={formData.IsPCBRelated?.value === 'Yes' || formData.IsPCBRelated === 'Yes' ? 'ncr-required' : ''}>
                  {formData.IsPCBRelated?.value === 'Yes' || formData.IsPCBRelated === 'Yes' ? (
                    <span className="text-danger">* </span>
                  ) : null}
                  PCB Serial Number:
                </span>
                <InfoTooltip message="Serial number associated with the PCB" />
              </label>
              <input
                type="text"
                id="PCBSerialNumber"
                name="PCBSerialNumber"
                value={formData.PCBSerialNumber}
                onChange={handleChange}
                placeholder="Enter PCB Serial Number"
                required={formData.IsPCBRelated?.value === 'Yes' || formData.IsPCBRelated === 'Yes'}
              />

              <label htmlFor="IssueType">
                <span className="ncr-required">
                  <span className="text-danger">* </span>Issue Type:
                </span>
              </label>
              <ReactSelect
                id="IssueType"
                name="IssueType"
                value={issueTypeOptions.find(option => option.value === (formData.IssueType?.value || formData.IssueType)) || null}
                onChange={(selectedOption) => handleSelectChange(selectedOption, 'IssueType')}
                options={issueTypeOptions}
                placeholder="Select Issue Type"
              />
            </>
          )}

          {/* Raised By */}
          <h4>Raised By</h4>
          
          {/* Global Location Raised By - Hidden (auto-derived from site selection) */}
          <div style={{ display: 'none' }}>
            <label htmlFor="GlobalSiteRaisedBy">
              <span className="ncr-required">
                <span className="text-danger">* </span>Global Location Raised By:
              </span>
            </label>
            <ReactSelect
              id="GlobalSiteRaisedBy"
              name="GlobalSiteRaisedBy"
              value={globalSiteOptionsRaisedBy.find(opt => opt.value === (formData.GlobalSiteRaisedBy?.value || formData.GlobalSiteRaisedBy)) || null}
              required
              isLoading={!!loadingGlobalSiteOptions}
              onChange={(selectedOption) =>
                handleSelectChange(selectedOption, 'GlobalSiteRaisedBy')
              }
              options={globalSiteOptionsRaisedBy}
              placeholder={loadingGlobalSiteOptions ? "Loading sites..." : "Select Global Location Raised By"}
            />
          </div>

          {/* Site Raised By */}
          <label htmlFor="SiteRaisedBy">
            <span className="ncr-required">
              <span className="text-danger">* </span>Local Site Raise By:
            </span>
          </label>
          <ReactSelect
            id="SiteRaisedBy"
            name="SiteRaisedBy"
            value={siteOptionsRaisedBy.find(opt => opt.value === (formData.SiteRaisedBy?.value || formData.SiteRaisedBy)) || null}
            required
            isLoading={loadingFilteredOptions}
            isDisabled={false}
            onChange={(selectedOption) =>
              handleSelectChange(selectedOption, 'SiteRaisedBy')
            }
            options={siteOptionsRaisedBy}
            placeholder={loadingFilteredOptions ? "Loading sites..." : "Select Local Site Raise By"}
          />

          {/* Department Raised By */}
          <label htmlFor="DepartmentRaisedBy">
            <span className="ncr-required">
              <span className="text-danger">* </span>Department Raised By:
            </span>
          </label>
          <ReactSelect
            id="DepartmentRaisedBy"
            name="DepartmentRaisedBy"
            value={departmentOptionsRaisedBy.find(opt => opt.value === (formData.DepartmentRaisedBy?.value || formData.DepartmentRaisedBy)) || null}
            required
            isLoading={!!(loadingFilteredOptions && selectedGlobalSiteRaisedBy)}
            isDisabled={!selectedGlobalSiteRaisedBy}
            onChange={(selectedOption) =>
              handleSelectChange(selectedOption, 'DepartmentRaisedBy')
            }
            options={departmentOptionsRaisedBy}
            placeholder={!selectedGlobalSiteRaisedBy ? "Select Global Location first" : (loadingFilteredOptions ? "Loading valid departments..." : "Select Department Raised By")}
          />

          {/* Raised By */}
          <label htmlFor="RaisedBy">
            <span className="ncr-required">
              <span className="text-danger">* </span>Raised By:
            </span>
          </label>
          <input 
            type="text" 
            id="RaisedBy" 
            name="RaisedBy" 
            value={formData.RaisedBy} 
            onChange={handleChange} 
            autoComplete="off" 
            required 
            placeholder="Enter Raised By"
          />

          {/* Assigned To */}
          <h4>Assigned To</h4>
          
          {/* Global Assigned Location - Hidden (auto-derived from site selection) */}
          <div style={{ display: 'none' }}>
            <label htmlFor="GlobalSiteAssigned">
              <span className="ncr-required">
                <span className="text-danger">* </span>Global Assigned Location:
              </span>
            </label>
            <ReactSelect
              id="GlobalSiteAssigned"
              name="GlobalSiteAssigned"
              value={globalSiteOptionsAssigned.find(opt => opt.value === (formData.GlobalSiteAssigned?.value || formData.GlobalSiteAssigned)) || null}
              required
              isLoading={!!loadingGlobalSiteOptions}
              onChange={(selectedOption) =>
                handleSelectChange(selectedOption, 'GlobalSiteAssigned')
              }
              options={globalSiteOptionsAssigned}
              placeholder={loadingGlobalSiteOptions ? "Loading sites..." : "Select Global Assigned Location"}
            />
          </div>

          {/* Assigned Site */}
          <label htmlFor="SiteAssigned">
            <span className="ncr-required">
              <span className="text-danger">* </span>Local Assigned Location:
            </span>
          </label>
          <ReactSelect
            id="SiteAssigned"
            name="SiteAssigned"
            value={siteOptionsAssigned.find(opt => opt.value === (formData.SiteAssigned?.value || formData.SiteAssigned)) || null}
            required
            isLoading={loadingFilteredOptions}
            isDisabled={false}
            onChange={(selectedOption) =>
              handleSelectChange(selectedOption, 'SiteAssigned')
            }
            options={siteOptionsAssigned}
            placeholder={loadingFilteredOptions ? "Loading sites..." : "Select Local Assigned Location"}
          />

          {/* Assigned Department */}
          <label htmlFor="DepartmentAssigned">
            <span className="ncr-required">
              <span className="text-danger">* </span>Assigned Department:
            </span>
            <InfoTooltip message="Source of In process Issue" />
          </label>
          <ReactSelect
            id="DepartmentAssigned"
            name="DepartmentAssigned"
            value={departmentOptionsAssigned.find(opt => opt.value === (formData.DepartmentAssigned?.value || formData.DepartmentAssigned)) || null}
            required
            isLoading={!!(loadingFilteredOptions && selectedGlobalSiteAssigned && selectedSiteAssigned)}
            isDisabled={!selectedGlobalSiteAssigned || !selectedSiteAssigned}
            onChange={(selectedOption) =>
              handleSelectChange(selectedOption, 'DepartmentAssigned')
            }
            options={departmentOptionsAssigned}
            placeholder={!selectedGlobalSiteAssigned || !selectedSiteAssigned ? "Select Global Assigned Location and Assigned Site first" : (loadingFilteredOptions ? "Loading valid departments..." : "Select Assigned Department")}
          />

          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={!canSubmit || isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit IPI'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default IPIForm;
